import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import Products from 'merchandise-lib/constants/Product';
import { useMemo } from 'react';
import { buildFilteredHighlightedProductsV2, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { useAppSelector } from '../../../reducers';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { sortApiNamesByProductLevel, sortSkuCategoryNames } from '../../../utils/sortingUtils';
import { CustomError } from '../../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../../v2/components/error-boundary/withErrorBoundary';
import { skuCategoryNameToProductLine } from '../../../v2/constants';
import { useBadges } from '../../../v2/hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../../v2/hooks/features/useFeatures';
import { useBundledSkuCategoryNamesFromApiNames } from '../../../v2/hooks/product/useProductDetails';
import { isValidProductLevel } from '../../../v2/utils/typeguards';
import ProductFeatureTableHeaderCell from '../product-feature-table/ProductFeatureTableHeaderCell';
import { FeatureTableLabelContent } from '../shared/FeatureTableLabelContent';
import GenericBundleFeatureTableHeaderCell from './GenericBundleFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
const buildFeatureTableHeaderData = (apiNames, screen, trackingSource, badges) => {
  const sortedApiNames = sortApiNamesByProductLevel(apiNames);
  return apiNames.reduce((headerDataMap, apiName, index) => {
    var _badges$apiName, _badges$apiName2;
    headerDataMap[apiName] = {
      Content: apiName === Products.HUBSPOT_FREE ? /*#__PURE__*/_jsx(ProductFeatureTableHeaderCell, {
        apiName: apiName,
        badgeName: badges === null || badges === void 0 || (_badges$apiName = badges[apiName]) === null || _badges$apiName === void 0 ? void 0 : _badges$apiName.badgeName
      }, apiName) : /*#__PURE__*/_jsx(GenericBundleFeatureTableHeaderCell, {
        apiName: sortedApiNames[index],
        screen: screen,
        trackingSource: trackingSource,
        badgeName: badges === null || badges === void 0 || (_badges$apiName2 = badges[apiName]) === null || _badges$apiName2 === void 0 ? void 0 : _badges$apiName2.badgeName
      }, apiName)
    };
    return headerDataMap;
  }, {});
};
const buildFeatureTableBodyData = (features, productLevelsFromBundle, skuCategoryNames, screen) => {
  const sectionData = skuCategoryNames.reduce((sectionDataMap, skuCategoryName) => {
    const productLine = skuCategoryNameToProductLine[skuCategoryName];
    sectionDataMap[skuCategoryName] = {
      features: features[skuCategoryName] || [],
      LabelContent: /*#__PURE__*/_jsx(FeatureTableLabelContent, {
        skuCategoryName: skuCategoryName
      }),
      apiNames: productLine ? buildFilteredHighlightedProductsV2({
        productLine,
        productLevels: productLevelsFromBundle
      }) : []
    };
    return sectionDataMap;
  }, {});
  if (Object.keys(sectionData).length > 0) {
    return {
      sharedData: {
        subscreen: screen
      },
      sectionData
    };
  }
};
const GenericBundleFeatureTable = ({
  apiNames,
  screen,
  trackingSource
}) => {
  const {
    showBoundary
  } = useErrorBoundary();
  const {
    badges
  } = useBadges({
    productApiNames: apiNames
  });
  const isSeatsRedesignUngated = useAppSelector(isUngatedForSeatsSelector);
  const {
    bundledSkuCategoryNames,
    loading: bundledSkuCategoryNamesLoading,
    error: bundledSkuCategoryNamesError
  } = useBundledSkuCategoryNamesFromApiNames({
    productApiNames: apiNames
  });
  const productLevelsFromBundle = useMemo(() => apiNames.map(apiName => {
    const level = getProductLevelFromApiNameString(apiName);
    return level;
  }).filter(isValidProductLevel), [apiNames]);
  if (bundledSkuCategoryNamesError) {
    showBoundary(new CustomError('Error fetching bundled sku category names', {
      bundledSkuCategoryNamesError,
      apiNames,
      trackingSource
    }));
  }
  if (!productLevelsFromBundle || productLevelsFromBundle.length === 0) {
    showBoundary(new CustomError('Invalid product levels', {
      productLevelsFromBundle,
      trackingSource
    }));
  }
  const sortedSkuCategoryNames = sortSkuCategoryNames(bundledSkuCategoryNames);
  const skuCategoryNameToApiName = useMemo(() => sortedSkuCategoryNames.reduce((acc, skuCategoryName) => {
    const productLine = skuCategoryNameToProductLine[skuCategoryName];
    if (productLine) {
      acc[skuCategoryName] = buildFilteredHighlightedProductsV2({
        productLine,
        productLevels: productLevelsFromBundle
      });
    }
    return acc;
  }, {}), [sortedSkuCategoryNames, productLevelsFromBundle]);
  const {
    featuresBySkuCategoryName,
    loading: featuresLoading,
    error: featuresError
  } = usePinnedFeaturesBySkuCategoryName(skuCategoryNameToApiName);
  const loading = featuresLoading || bundledSkuCategoryNamesLoading;
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (featuresError) {
    showBoundary(new CustomError(featuresError.message || 'Error fetching features', {
      featuresError,
      productLevelsFromBundle,
      apiNames,
      skuCategoryNameToApiName,
      trackingSource,
      fingerprint: ['errorFetchingFeatures']
    }));
  }
  if (!featuresBySkuCategoryName || !Object.keys(featuresBySkuCategoryName).length) {
    return null;
  }
  const headerData = buildFeatureTableHeaderData(apiNames, screen, trackingSource, badges);
  const bodyData = buildFeatureTableBodyData(featuresBySkuCategoryName, productLevelsFromBundle, sortedSkuCategoryNames, screen);
  if (!bodyData) {
    return null;
  }
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: isSeatsRedesignUngated,
    isInbound25PricingPage: true
  });
};
export default withErrorBoundary(GenericBundleFeatureTable, {
  fallback: null,
  errorBoundaryName: 'GENERIC_BUNDLE_FEATURE_TABLE'
});